package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.ClockAction
import com.evoapps.evoshift.domain.model.KioskClockResult
import com.evoapps.evoshift.domain.model.KioskOfflineException
import com.evoapps.evoshift.domain.model.KioskQrCode
import com.evoapps.evoshift.domain.repository.KioskRepository
import com.evoapps.evoshift.kiosk.QrScan
import com.evoapps.evoshift.kiosk.QrScanner
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.qr_camera_denied
import evoshift.composeapp.generated.resources.qr_not_a_tablet_code
import evoshift.composeapp.generated.resources.qr_offline
import evoshift.composeapp.generated.resources.qr_scanner_unavailable
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class QrClockUiState(
    /** The device can scan (hidden on the desktop and in browsers without BarcodeDetector). */
    val isAvailable: Boolean,
    /** The scanner is open or the clock request is on its way. */
    val isBusy: Boolean = false,
    /** Started or ended, at what time ("HH:mm"); shown until dismissed. */
    val result: KioskClockResult? = null,
    val error: UiText? = null
)

/**
 * SH-111, the phone's "Scan the tablet's code": opens the camera scanner, sends the code to
 * clockWithQr as the signed-in person and shows what happened. The shift itself updates through
 * the dashboard's live listener; nothing is changed here before the server says so (the server
 * decides in or out by the same clock rules as the button).
 */
class QrClockViewModel(
    private val repository: KioskRepository,
    private val scanner: QrScanner
) : ViewModel() {
    private val _uiState = MutableStateFlow(QrClockUiState(isAvailable = scanner.isAvailable))
    val uiState: StateFlow<QrClockUiState> = _uiState.asStateFlow()

    /** Scans and clocks; SH-116: [startBreak] starts a break instead (else the server decides). */
    fun scan(startBreak: Boolean = false) {
        if (_uiState.value.isBusy) return
        _uiState.update { it.copy(isBusy = true, result = null, error = null) }
        viewModelScope.launch {
            when (val scan = scanner.scan()) {
                is QrScan.Code -> clock(scan.text, if (startBreak) ClockAction.BREAK_START else null)
                QrScan.Cancelled -> _uiState.update { it.copy(isBusy = false) }
                QrScan.PermissionDenied -> fail(UiText.Resource(Res.string.qr_camera_denied))
                QrScan.Unavailable -> fail(UiText.Resource(Res.string.qr_scanner_unavailable))
            }
        }
    }

    private suspend fun clock(text: String, action: ClockAction?) {
        // Someone else's QR (a menu, a Wi-Fi code) never reaches the server.
        if (!KioskQrCode.looksLikeToken(text)) return fail(UiText.Resource(Res.string.qr_not_a_tablet_code))
        repository.clockWithQr(text, action).fold(
            onSuccess = { result -> _uiState.update { it.copy(isBusy = false, result = result) } },
            onFailure = { e ->
                fail(if (e is KioskOfflineException) UiText.Resource(Res.string.qr_offline) else e.toUiText())
            }
        )
    }

    private fun fail(message: UiText) = _uiState.update { it.copy(isBusy = false, error = message) }

    fun dismiss() = _uiState.update { it.copy(result = null, error = null) }
}
